import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { reviewApi, type PublicReview } from '../../services/reviewApi';
import Card from '../ui/Card';

import ReviewItem from './ReviewItem';
import ReviewsModalShell from './ReviewsModalShell';

const PAGE_SIZE = 10;
const PREVIEW_COUNT = 2;

interface Props {
  activityId: string;
}

/**
 * Read-only public reviews on an activity detail page. Shows a short preview and
 * a "查看全部" entry; all write/edit/delete lives in the dashboard "我的评论" tab.
 */
export default function ActivityReviews({ activityId }: Props) {
  const { t, i18n } = useTranslation('review');

  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Monotonic guard: only the most recent list request may write state.
  const listReqRef = useRef(0);
  const loadList = useCallback(
    async (nextPage: number) => {
      const reqId = ++listReqRef.current;
      setLoading(true);
      try {
        const data = await reviewApi.listForActivity(activityId, nextPage, PAGE_SIZE);
        if (reqId !== listReqRef.current) return; // superseded by a newer load
        setTotal(data.total);
        setPage(data.page);
        setReviews((prev) => (nextPage === 1 ? data.items : [...prev, ...data.items]));
      } catch {
        // Public list failure is non-blocking; leave existing items.
      } finally {
        if (reqId === listReqRef.current) setLoading(false);
      }
    },
    [activityId]
  );

  useEffect(() => {
    loadList(1);
  }, [loadList]);

  return (
    <div id="activity-reviews" className="scroll-mt-24">
      <Card padding="lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('title')}</h2>
          {total > 0 && (
            <span className="text-sm text-gray-500">{t('reviewCount', { count: total })}</span>
          )}
        </div>

        {reviews.length === 0 && !loading ? (
          <p className="text-sm text-gray-500">{t('empty')}</p>
        ) : (
          <ul className="space-y-5">
            {reviews.slice(0, PREVIEW_COUNT).map((r) => (
              <ReviewItem key={r.id} review={r} language={i18n.language} />
            ))}
          </ul>
        )}

        {total > PREVIEW_COUNT && (
          <button
            onClick={() => setShowAll(true)}
            className="mt-5 text-sm text-primary hover:underline font-medium"
          >
            {t('showAll', { count: total })}
          </button>
        )}
      </Card>

      {showAll && (
        <ReviewsModalShell total={total} onClose={() => setShowAll(false)}>
          <ul className="space-y-5">
            {reviews.map((r) => (
              <ReviewItem key={r.id} review={r} language={i18n.language} />
            ))}
          </ul>
          {reviews.length < total && (
            <button
              onClick={() => loadList(page + 1)}
              disabled={loading}
              className="mt-5 text-sm text-primary hover:underline font-medium disabled:opacity-50"
            >
              {t('loadMore')}
            </button>
          )}
        </ReviewsModalShell>
      )}
    </div>
  );
}
